
import { Options, Vue } from 'vue-class-component'
import { maska } from 'maska'
import { Watch } from 'vue-property-decorator'
import { ACTION_FORM, ACTION_GOAL, ACTION_MESSAGES, ACTION_QUESTION, USER_ANSWER } from '@/store/actions'
import {
  EScenarioResourceType,
  IAnswer,
  IButtonAnswer,
  IDateRangePicker,
  IForm,
  IGoal,
  IImageAnswer,
  IMessage,
  IQuestion,
  ISelectedItemCondition,
  ITextAnswer,
  IVersionHistory,
} from '@/utils/types'
import { randomString } from '@/utils/helpers'
import { constant, SCENARIO_MAKER } from '@/utils/constants'
import cloneDeep from 'lodash/cloneDeep'
import { ChatBotFlowsMaker } from 'bot-flow-maker/src/index'
import { ICard, ICardAnswer, ICardType } from 'bot-flow-maker/src/types'
import debounce from 'quasar/src/utils/debounce.js';
import { DatetimeUtils } from '@/utils/datetime'
import CLoading from '@/components/common/ui/CLoading.vue'
import ScenarioConditionMenu from '@/components/scenarios/ui/ScenarioConditionMenu.vue'
import ScenarioHeader from '@/components/story/scenarios/ScenarioHeader.vue'
import { ObjectUtils } from '@/utils/objects'

@Options({
  components: {
    ScenarioHeader,
    CLoading,
    ScenarioConditionMenu,
  },
  directives: { maska },
})
export default class ScenariosMakerFrame extends Vue {
  selectedEditItem!: ICard
  value: ICard[] = []
  localValue: ICard[] = []
  chatbotFlow!: ChatBotFlowsMaker
  defaultAnswer = false
  currentZoom = 100
  isDisplayMenu = false
  isOpen = false
  dateRange: IDateRangePicker = DatetimeUtils.getDateRange('last30Days')

  titleLastHistory = ''
  loadingIFrame = true
  loadingResource = true
  directionType = '1'
  button = {
    actions: [
      {
        content: '',
        nextCards: [],
      },
    ],
  }

  cardTypes: ICardType[] = [
    {
      name: EScenarioResourceType.question,
      displayText: 'Q/A',
    },
    {
      name: EScenarioResourceType.message,
      displayText: 'Message',
    },
    {
      name: EScenarioResourceType.goal,
      displayText: 'Goal',
    },
    {
      name: EScenarioResourceType.form,
      displayText: 'Form',
    },
  ]

  menuAnchor = 'bottom right'
  menuSelf = 'top start'
  selectedMenuAnchor = 'top right'
  selectedMenuSelf = 'top start'
  totalUsersReport = []
  newPush = false

  get loading() {
    return this.loadingIFrame && this.loadingResource
  }

  get appId() {
    return this.$route?.params?.appId
  }

  get isReport() {
    return this.$route?.params?.isReport === 'true'
  }

  get campaignId() {
    return this.$route?.params?.campaignId
  }

  get isTestMode() {
    return this.$route?.params?.isTestMode === 'true'
  }

  get isReadOnly() {
    return this.$route?.params?.isReadOnly === 'true'
  }

  get campaignType() {
    return this.$route?.params?.campaignType
  }

  get messages() {
    return this.$store.getters.messages
  }

  get questions() {
    return this.$store.getters.questions
  }

  get goals() {
    return this.$store.getters.goals
  }

  get forms() {
    return this.$store.getters.forms
  }

  get isBlankPush() {
    return this.$route?.query?.isBlankPush === 'true'
  }

  get isHideHeader() {
    return !!this.$route?.query?.isHideHeader
  }

  get typeMapping() {
    return {
      [EScenarioResourceType.question]: this.$t('menu.q_a'),
      [EScenarioResourceType.message]: this.$t('menu.message'),
      [EScenarioResourceType.goal]: this.$t('menu.goal'),
      [EScenarioResourceType.form]: this.$t('menu.form'),
      [EScenarioResourceType.start]: this.$t('label.start'),
    }
  }

  @Watch('appId', { immediate: true })
  async appIdChanged() {
    if (!this.appId) {
      return
    }
    this.loadingResource = true
    await this.loadResources()
    this.loadingResource = false
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.READY,
        campaignId: this.campaignId,
        isReadOnly: this.isReadOnly,
      },
      '*'
    )
  }

  async loadResources() {
    await this.$store.dispatch(ACTION_QUESTION.LOAD_ITEMS, {
      app_id: this.appId,
    })

    await this.$store.dispatch(ACTION_GOAL.LOAD_ITEMS, {
      app_id: this.appId,
    })

    await this.$store.dispatch(ACTION_FORM.LOAD_ITEMS, {
      app_id: this.appId,
    })

    await this.$store.dispatch(ACTION_MESSAGES.LOAD_ITEMS, {
      app_id: this.appId,
    })

    if (this.isReport) {
      await this.dateRangeChanged()
    }
  }

  get actions() {
    if (this.button) {
      return this.button.actions?.filter((p) => p.content !== undefined && p.content !== '')
    }
    return []
  }

  getResourceByCard(card) {
    let resource

    if (card.cardType === EScenarioResourceType.question) {
      resource = this.questions.find((question) => question._id === card.id)
    } else if (card.cardType === EScenarioResourceType.message) {
      resource = this.messages.find((message) => message._id === card.id)
    } else if (card.cardType === EScenarioResourceType.goal) {
      resource = this.goals.find((goal) => goal._id === card.id)
    } else if (card.cardType === EScenarioResourceType.form) {
      resource = this.forms.find((form) => form._id === card.id)
    }
    return resource
  }

  addCardToLocalValue(resource, cardType: string, selectedItemCondition: ISelectedItemCondition) {
    const nextUniqueId = randomString()
    const answersData: ICardAnswer[] = [
      {
        id: 'any',
        title: this.$t('label.all_users'),
        nextCards: [],
        totalUsers: 0,
      },
    ]
    if (cardType !== EScenarioResourceType.message) {
      if (resource && resource.answers) {
        const answers: ITextAnswer[] | IImageAnswer[] | IButtonAnswer | undefined = ObjectUtils.getAnswers(
          resource.answers
        )
        answers.forEach((element) => {
          const answer: ICardAnswer = {
            id: element._id ?? '',
            title: element?.label || element?.title || this.$t('label.all_users'),
            nextCards: [],
            totalUsers: 0,
          }

          answersData.push(answer)
        })
      }
    }

    const card: ICard = {
      id: resource._id ?? '',
      displayId: '',
      uniqueId: nextUniqueId,
      cardType: cardType,
      title: resource.title ?? '',
      titleBadge: this.typeMapping[cardType],
      ...this.getMainContainerMiddlePos(),
      answers: answersData,
      totalUsers: 0,
      labelTotalUsers: this.$t('label.user'),
    }

    this.chatbotFlow.addCard(card)
    this.localValue.push(card)
    this.updateNextCards(nextUniqueId, selectedItemCondition)
    this.chatbotFlow.render()
  }

  // addWelcomeCard(resource) {
  //   const card = this.localValue.find((item) => item.uniqueId === EScenarioResourceType.welcome)
  //   if (card) {
  //     card.id = resource._id
  //   }
  //   this.chatbotFlow.updateCard(EScenarioResourceType.welcome, card)
  //   this.chatbotFlow.render()
  // }

  updateCard(resource) {
    for (const item of this.localValue) {
      if (item.id === resource._id) {
        item.title = resource.title
        this.chatbotFlow.updateCard(item.uniqueId, item)
      }
    }
    this.chatbotFlow.render()
  }

  updateNextCards(toUniqueId, selectedItemCondition: ISelectedItemCondition) {
    const card = this.localValue.find((item) => item.uniqueId === selectedItemCondition.uniqueId)
    if (card) {
      const answers = card.answers
      let selectedAnswerIds = selectedItemCondition.selectedAnswerIds
      if (selectedAnswerIds) {
        if (selectedAnswerIds.includes('any')) {
          answers.forEach((item) => {
            item.nextCards = []
          })
          selectedAnswerIds = ['any']
        } else {
          const anyAnswer = answers.find((item) => item.id === 'any')
          if (anyAnswer) {
            anyAnswer.nextCards = []
          }
        }
        selectedAnswerIds.forEach((id) => {
          const answer = answers.find((item) => item.id === id)
          if (answer) {
            let nodeIndex = 1
            if (this.directionType === '0') {
              nodeIndex = 7
            }
            answer.nextCards = []
            answer.nextCards.push({
              nodeIndex: nodeIndex,
              uniqueId: toUniqueId,
            })
          }
        })
      }
    }

    this.chatbotFlow.updateCard(selectedItemCondition.uniqueId ?? '', card)
  }

  @Watch('localValue', { deep: true })
  localValueChanged() {
    const localValue = cloneDeep(this.localValue)
    this.value = localValue
    debounce(() => {
      this.sendDataToParent()
      this.setParentEditingMode(0)
    }, 100).call(this)
  }

  getMainContainer() {
    return document.getElementsByTagName('BODY')[0] as HTMLElement
  }

  getMainContainerMiddlePos() {
    const mainContainer = this.getMainContainer()
    const bounding = mainContainer.getBoundingClientRect()
    const top = mainContainer.scrollTop | Math.abs(bounding.top)
    const left = mainContainer.scrollLeft | Math.abs(bounding.left)
    return {
      left: left + 200,
      top: top + 200,
    }
  }

  onChangeCards(newValue) {
    this.localValue = newValue
  }

  onCardEdit(uniqueId) {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.ON_CARD_EDIT,
        campaignId: this.campaignId,
        data: uniqueId,
      },
      '*'
    )
  }

  onTransmissionLog() {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.ON_OPEN_LOG,
        campaignId: this.campaignId,
        data: '',
      },
      '*'
    )
  }

  onOpenPreview() {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.ON_OPEN_PREVIEW,
        campaignId: this.campaignId,
        data: '',
      },
      '*'
    )
  }

  openSelectNextQuestion(data) {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.OPEN_SELECT_NEXT_QUESTION,
        campaignId: this.campaignId,
        data: data,
      },
      '*'
    )
  }

  openSelectNextMessage(data) {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.OPEN_SELECT_NEXT_MESSAGE,
        campaignId: this.campaignId,
        data: data,
      },
      '*'
    )
  }

  openSelectNextGoal(data) {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.OPEN_SELECT_NEXT_GOAL,
        campaignId: this.campaignId,
        data: data,
      },
      '*'
    )
  }

  openSelectNextForm(data) {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.OPEN_SELECT_NEXT_FORM,
        campaignId: this.campaignId,
        data: data,
      },
      '*'
    )
  }

  onAddCard(uniqueId) {
    this.isDisplayMenu = true
    const item = this.localValue.find((item) => item.uniqueId === uniqueId)
    if (item) {
      const html = document.documentElement
      const cardEl = document.getElementById(item.uniqueId) as HTMLElement
      const btnAdd = cardEl.querySelector('#btnAdd') as HTMLElement
      const bounding = btnAdd.getBoundingClientRect()
      if (html.offsetWidth - bounding.left < 150) {
        this.menuAnchor = 'bottom left'
        this.menuSelf = 'top end'
      } else {
        this.menuAnchor = 'bottom right'
        this.menuSelf = 'top start'
      }
      if (html.offsetWidth - bounding.left < 300) {
        this.selectedMenuAnchor = 'top left'
        this.selectedMenuSelf = 'top end'
      } else {
        this.selectedMenuAnchor = 'top right'
        this.selectedMenuSelf = 'top start'
      }
      this.selectedEditItem = item
    }
  }

  async filterUserAnaswersByDateRange() {
    this.totalUsersReport = await this.$store.dispatch(USER_ANSWER.TOTAL_USERS_REPORT_SCENARIO, {
      app_id: this.appId,
      campaign_id: this.campaignId,
      is_test_mode: this.isTestMode,
      date_from: DatetimeUtils.formatDate(this.dateRange.startDate),
      date_to: DatetimeUtils.formatDate(this.dateRange.endDate),
    })
  }

  countUsersByUniqueId(prefix: string) {
    let total = 0
    for (const item of this.totalUsersReport) {
      // eslint-disable-next-line
      const key = item['unique_id'] + '_' + item['answer_id']
      if (key.startsWith(prefix)) {
        // eslint-disable-next-line
        total = total + item['total']
      }
    }

    return total
  }

  /**
   * localValue contains itemData, we do not store this field
   */
  makeLocalValue(isCopy = false) {
    this.chatbotFlow.removeAllCards()
    const cards: ICard[] = cloneDeep(this.value) || []
    for (const card of cards) {
      if (card.uniqueId === EScenarioResourceType.start) {
        // if (this.newPush) {
        //   this.onCardEdit(card.uniqueId)
        // }
        card.titleBadge = ''
        card.title = this.$t('label.start')
        const answers = card.answers.find((item) => item.id === 'any')
        const answersData: ICardAnswer[] = []
        const anyCardAnswer: ICardAnswer = {
          id: 'any',
          title: this.$t('label.any'),
          nextCards: answers?.nextCards || [],
          totalUsers: 0,
        }
        answersData.push(anyCardAnswer)
        card.answers = answersData
        this.newPush = false
      } else {
        // if (card.uniqueId === EScenarioResourceType.welcome && !card.id && !isCopy) {
        //   this.onCardEdit(card.uniqueId)
        // }
        const resource = this.getResourceByCard(card)
        if (resource) {
          const answers = card.answers.find((item) => item.id === 'any')
          const answersData: ICardAnswer[] = []
          const anyCardAnswer: ICardAnswer = {
            id: 'any',
            title: this.$t('label.any'),
            nextCards: answers?.nextCards || [],
            totalUsers: 0,
          }

          card.displayId = ''
          card.title = resource.title ?? ''
          card.titleBadge = this.typeMapping[card.cardType]

          if (this.isReport) {
            card.totalUsers = this.countUsersByUniqueId(card.uniqueId)
            let totalDetailUsers = 0
            if (card.totalUsers !== 0 && answers?.nextCards && answers?.nextCards.length > 0) {
              const key = answers?.nextCards[0].uniqueId
              totalDetailUsers = this.countUsersByUniqueId(key)
            }
            anyCardAnswer.totalUsers = totalDetailUsers
          }

          answersData.push(anyCardAnswer)
          if (card.cardType !== EScenarioResourceType.message && card.cardType !== EScenarioResourceType.form) {
            const answers: ITextAnswer[] | IImageAnswer[] | undefined = ObjectUtils.getAnswers(resource.answers)
            answers.forEach((elm) => {
              const cardAnswer = card.answers.find((item) => item.id === elm._id)
              let totalDetailUsers = 0
              if (card.totalUsers !== 0 && this.isReport && cardAnswer?.nextCards && cardAnswer?.nextCards.length > 0) {
                const key = cardAnswer?.nextCards[0].uniqueId + '_' + elm._id
                totalDetailUsers = this.countUsersByUniqueId(key)
              }

              answersData.push({
                id: elm._id ?? '',
                title: elm?.label || elm?.title || this.$t('label.any'),
                nextCards: cardAnswer?.nextCards || [],
                totalUsers: totalDetailUsers,
              })
            })
          }
          card.answers = answersData
        }
        card.labelTotalUsers = this.$t('label.user')
      }

      this.chatbotFlow.addCard(card)
    }

    this.localValue = cards
    this.chatbotFlow.render()
  }

  onUpdateDone(data: IQuestion | IMessage | IGoal | IForm) {
    this.updateCard(data)
  }

  onSelectNextDone(
    data: IQuestion | IMessage | IGoal | IForm,
    type: EScenarioResourceType,
    selectedItemCondition: ISelectedItemCondition
  ) {
    // if (type === EScenarioResourceType.welcome) {
    //   this.addWelcomeCard(data)
    // } else {
    this.addCardToLocalValue(data, type, selectedItemCondition)
    // }
  }

  onReUndoCards(data) {
    this.value = data
    this.makeLocalValue()
  }

  initScenarios(data) {
    this.value = data
    this.initChatBotFlowsMaker()
    this.makeLocalValue()
  }

  onReceiveValueFromParent(data: ICard[]) {
    this.value = data
    this.makeLocalValue()
    this.setScrollLeftToCenter()
  }

  onReceiveValueFromTemplate(data: ICard[]) {
    this.value = data
    this.makeLocalValue(true)
    this.setScrollLeftToCenter()
  }

  setScrollLeftToCenter() {
    const html = document.documentElement
    let left = (html.scrollWidth - html.clientWidth) / 2
    if (this.value.length > 0) {
      for (const card of this.value) {
        if (left === 0) {
          left = card.left
        } else {
          if (card.left < left) {
            left = card.left
          }
        }
      }
    }
    html.scrollLeft = left
  }

  sendDataToParent() {
    if (this.isReport) {
      return
    }

    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.IFRAME_NEW_DATA,
        campaignId: this.campaignId,
        data: JSON.stringify(this.value),
      },
      '*'
    )
  }

  /**
   * @param value: 0: not editing |1: editing (has modal opening)
   */
  setParentEditingMode(value: number) {
    if (this.isReport) {
      return
    }
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.IS_EDITING,
        campaignId: this.campaignId,
        data: value,
      },
      '*'
    )
  }

  @Watch('dateRange')
  async dateRangeChanged() {
    await this.filterUserAnaswersByDateRange()
    this.makeLocalValue()
  }

  postRangeCondtion() {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.DATE_RANGE,
        campaignId: this.campaignId,
        data: cloneDeep(this.dateRange),
      },
      '*'
    )
  }

  initChatBotFlowsMaker() {
    const container = document.getElementById('mainContainer')
    const btnNext = document.getElementById('btnNext') as HTMLButtonElement
    const btnAdd = document.getElementById('btnAdd') as HTMLButtonElement
    const btnEdit = document.getElementById('btnEdit') as HTMLButtonElement
    const btnDelete = document.getElementById('btnDelete') as HTMLButtonElement
    const iconStart = document.getElementById('iconStart') as HTMLElement
    const iconMessage = document.getElementById('iconMessage') as HTMLElement
    const iconQuestion = document.getElementById('iconQuestion') as HTMLElement
    const iconGoal = document.getElementById('iconGoal') as HTMLElement

    if (btnNext) {
      this.chatbotFlow = new ChatBotFlowsMaker(
        container,
        btnNext,
        btnAdd,
        btnEdit,
        btnDelete,
        iconStart,
        iconMessage,
        iconQuestion,
        iconGoal,
        this.isReport,
        this.isReadOnly
      )
      this.chatbotFlow.onChange(this.onChangeCards)
      this.chatbotFlow.onCardEdit(this.onCardEdit)
      this.chatbotFlow.setCardTypes(this.cardTypes)
      this.chatbotFlow.onAddCard(this.onAddCard)
      if (container) {
        const rect = container.getBoundingClientRect()
        this.chatbotFlow.setBoundingClientRectBeforeZoom(rect)
      }
    }
  }

  onAddNextClicked(data) {
    this.isOpen = false
    if (data.cardType === EScenarioResourceType.question) {
      this.openSelectNextQuestion(data)
    } else if (data.cardType === EScenarioResourceType.goal) {
      this.openSelectNextGoal(data)
    } else if (data.cardType === EScenarioResourceType.form) {
      this.openSelectNextForm(data)
    } else {
      this.openSelectNextMessage(data)
    }
  }

  onSelectHistoryDone(item: IVersionHistory) {
    if (item.campaign) {
      let scenario
      if (this.isTestMode) {
        scenario = item.campaign.test_detail?.scenarios
      } else {
        scenario = item.campaign.prod_detail?.scenarios
      }
      this.value = scenario
      this.makeLocalValue()
    }
  }

  onHandleErrorCard(uniqueId) {
    for (const item of this.localValue) {
      const cardEl = document.getElementById(item.uniqueId) as HTMLElement
      cardEl.classList.remove('error-card')
    }

    const card = this.updateErrorCard(uniqueId)
    if (card) {
      const html = document.documentElement
      html.scrollLeft = card.left
      html.scrollTop = card.top - 200
      this.setNextTargetErrorCards(card)
      this.setPrevTargetErrorCards(card)
    }
  }

  updateErrorCard(uniqueId) {
    const card = this.localValue.find((item) => item.uniqueId === uniqueId)
    if (card) {
      const cardEl = document.getElementById(card.uniqueId) as HTMLElement
      cardEl.classList.add('error-card')
    }
    return card
  }

  setNextTargetErrorCards(card: ICard) {
    for (const answer of card.answers) {
      for (const next of answer.nextCards) {
        const child = this.localValue.find((item) => item.uniqueId === next.uniqueId)
        if (child) {
          if (child.cardType !== EScenarioResourceType.message) {
            return
          }

          this.updateErrorCard(child.uniqueId)
          this.setNextTargetErrorCards(child)
        }
      }
    }
  }

  setPrevTargetErrorCards(card: ICard) {
    for (const item of this.localValue) {
      for (const answer of item.answers) {
        for (const next of answer.nextCards) {
          if (next.uniqueId === card.uniqueId && item.cardType === EScenarioResourceType.message) {
            this.updateErrorCard(item.uniqueId)
            this.setPrevTargetErrorCards(item)
          }
        }
      }
    }
  }

  handleZoomInOut(zoomLevel) {
    const transform = this.$refs.panZoom.$panZoomInstance.getTransform()
    const deltaX = transform.x
    const deltaY = transform.y
    const scale = zoomLevel / 100
    // const scale = zoomLevel / 100
    const offsetX = scale + deltaX
    const offsetY = scale + deltaY
    this.$refs.panZoom.$panZoomInstance.zoomAbs(offsetX, offsetY, scale)
    this.chatbotFlow.setZoomLevel(zoomLevel)
  }

  onTransform() {
    if (this.chatbotFlow.getZoomLevel() !== this.currentZoom) {
      this.chatbotFlow.render()
      this.currentZoom = this.chatbotFlow.getZoomLevel()
    }
  }

  onPauseZoom() {
    this.$refs.panZoom.$panZoomInstance.pause()
  }

  onChangeDirection(value) {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.ON_CHANGE_DIRECTION,
        campaignId: this.campaignId,
        data: value,
      },
      '*'
    )

    this.chatbotFlow.setDirectionType(value)
    this.chatbotFlow.render()
  }

  onOpenHistory() {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.ON_OPEN_HISTORY,
        campaignId: this.campaignId,
        data: '',
      },
      '*'
    )
  }

  openCopyScenarios() {
    parent.postMessage(
      {
        action: SCENARIO_MAKER.FROM_IFRAME.ON_OPEN_COPY_SCENARIOS,
        campaignId: this.campaignId,
        data: '',
      },
      '*'
    )
  }

  setLoading(value) {
    this.loadingIFrame = value
  }

  setDirectionType(value) {
    this.directionType = value
    this.$refs.scenarioHeader.setDirectionType(value)
  }

  setTitleHistory(value) {
    this.titleLastHistory = value
  }

  handleKeyPress(event) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'z') {
      this.$refs.scenarioHeader.handleUndo()
    }
  }

  handleMessage(event) {
    if (!event.data.action) {
      return
    }

    const action = event.data.action
    const data = JSON.parse(event.data.data.data)
    const isTestMode = event.data.data.isTestMode
    if (isTestMode !== this.isTestMode) {
      // console.log('isTestMode 1:', isTestMode)
      return
    }

    if (action === SCENARIO_MAKER.FROM_PARENT.CURRENT_VALUE) {
      this.setLoading(true)
      this.onReceiveValueFromParent(data)
      this.setLoading(false)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.ADD_CARD) {
      this.onSelectNextDone(data.resource, data.cardType, data.selectedItemCondition)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.UPDATE_CARD) {
      this.onUpdateDone(data.resource)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.DIRECTION) {
      this.setDirectionType(data)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.MSG_LAST_HISTORY) {
      this.setTitleHistory(data)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.ITEM_HISTORY) {
      this.onSelectHistoryDone(data)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.ERROR_CARD) {
      this.onHandleErrorCard(data)
    } else if (action === SCENARIO_MAKER.FROM_PARENT.CTRL_Z) {
      this.$refs.scenarioHeader.handleUndo()
    } else if (action === SCENARIO_MAKER.FROM_PARENT.LOAD_SCENARIO) {
      this.setLoading(true)
      this.onReceiveValueFromTemplate(data)
      this.setLoading(false)
    }
  }

  async mounted() {
    window.addEventListener('click', () => {
      this.isDisplayMenu = false
    })
    window.addEventListener('message', async (event) => this.handleMessage(event))
    window.addEventListener('keydown', this.handleKeyPress)

    this.initChatBotFlowsMaker()
    if (this.campaignType === 'push' && this.isBlankPush) {
      this.newPush = true
    }
  }

  async beforeUnmount() {
    window.removeEventListener('click', () => {
      this.isDisplayMenu = false
    })
    window.removeEventListener('message', async (event) => this.handleMessage(event))
    window.removeEventListener('keydown', this.handleKeyPress)
  }
}
